/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * AudioDeviceSelect.tsx — Audi\1 \2evic\1 \2elect (services/operator-web-app).
 */

/**
 * E3-S2: UI for selecting preferred microphone and speaker.
 * Persists to localStorage; use getPreferredInputConstraints / getPreferredOutputId when starting media.
 */
import { useAudioDevices } from './useAudioDevices';
import { Button } from '@/components/ui';
import { useTranslation } from '@/i18n';

export function AudioDeviceSelect() {
  const t = useTranslation();
  const {
    inputs,
    outputs,
    preferredInputId,
    preferredOutputId,
    error,
    refresh,
    setPreferredInput,
    setPreferredOutput,
    requestPermissionAndRefresh,
  } = useAudioDevices();

  return (
    <section
      className="rounded-lg border p-3 text-sm glass-panel"
      aria-labelledby="audio-devices-heading"
    >
      <h2
        id="audio-devices-heading"
        className="font-medium mb-2"
        style={{ color: 'var(--text-primary)' }}
      >
        {t('audio.title')}
      </h2>
      {error && (
        <p className="text-amber-600 dark:text-amber-400 mb-2" role="alert">
          {error}
        </p>
      )}
      <div className="grid gap-3">
        <div>
          <label
            htmlFor="audio-input-select"
            className="block mb-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            {t('audio.microphone')}
          </label>
          <select
            id="audio-input-select"
            className="w-full rounded border px-2 py-1.5"
            style={{
              borderColor: 'var(--glass-border)',
              background: 'var(--toggle-bg)',
              color: 'var(--text-primary)',
            }}
            value={preferredInputId ?? ''}
            onChange={(e) => setPreferredInput(e.target.value ? e.target.value : null)}
            aria-describedby="audio-input-hint"
          >
            <option value="">{t('audio.default')}</option>
            {inputs.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
          <p id="audio-input-hint" className="sr-only">
            {t('audio.microphone_hint')}
          </p>
        </div>
        <div>
          <label
            htmlFor="audio-output-select"
            className="block mb-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            {t('audio.speakers')}
          </label>
          <select
            id="audio-output-select"
            className="w-full rounded border px-2 py-1.5"
            style={{
              borderColor: 'var(--glass-border)',
              background: 'var(--toggle-bg)',
              color: 'var(--text-primary)',
            }}
            value={preferredOutputId ?? ''}
            onChange={(e) => setPreferredOutput(e.target.value ? e.target.value : null)}
            aria-describedby="audio-output-hint"
          >
            <option value="">{t('audio.default')}</option>
            {outputs.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
          <p id="audio-output-hint" className="sr-only">
            {t('audio.speakers_hint')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => refresh()}>
            {t('audio.refresh')}
          </Button>
          {inputs.length === 0 && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => requestPermissionAndRefresh()}
            >
              {t('audio.allow_microphone')}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
